import { useEffect, useId, useRef, useState } from "react"
import {
  Building,
  ChevronRight,
  FileText,
  Image as ImageIcon,
  Info,
  LayoutGrid,
  Link as LinkIcon,
  Plus,
  Trash2,
  UploadCloud,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  archiveDealUnit,
  archiveDealFile,
  createDealUnit,
  getDealEnrichment,
  setDealCoverFile,
  updateDealEnrichment,
  updateDealUnit,
  uploadDealFile,
} from "@/lib/api"
import { looksPersistedDealId } from "@/lib/persisted-deal"
import { useSession } from "@/lib/session-context"
import { cn } from "@/lib/utils"

const DEFAULT_UNITS = [
  {
    id: 1,
    unit: "Flat 1",
    type: "1-Bed Flat",
    rent: 1200,
    status: "Tenanted",
    planning: "C3",
    tenancyType: "AST Fixed",
    endOfTenancy: "2025-06-01",
    breakClause: "",
    noticePeriod: "2",
    area: "450",
    annualRent: 14400,
  },
  {
    id: 2,
    unit: "Flat 2",
    type: "2-Bed Flat",
    rent: 1500,
    status: "Tenanted",
    planning: "C3",
    tenancyType: "AST Fixed",
    endOfTenancy: "",
    breakClause: "",
    noticePeriod: "2",
    area: "650",
    annualRent: 18000,
  },
  {
    id: 3,
    unit: "Commercial",
    type: "Retail",
    rent: 0,
    status: "Vacant",
    planning: "E",
    tenancyType: "Commercial Lease",
    endOfTenancy: "",
    breakClause: "",
    noticePeriod: "",
    area: "1200",
    annualRent: 0,
  },
]

const EMPTY_UNIT = {
  unit: "",
  type: "1-Bed Flat",
  physicalPropertyType: "Flat",
  bedroomCount: "1",
  rent: 0,
  status: "Tenanted",
  planning: "C3",
  tenancyType: "AST Fixed",
  endOfTenancy: "",
  breakClause: "",
  noticePeriod: "",
  area: "",
  annualRent: 0,
}

const PHYSICAL_PROPERTY_TYPES = [
  "Flat",
  "Terraced House",
  "Semi-Detached House",
  "Detached House",
  "House",
  "HMO Room (Non-Ensuite)",
  "HMO Room (Ensuite)",
  "Agricultural",
  "B&B / Guest House",
  "Industrial",
  "Land",
  "Office",
  "Retail",
]
const RESIDENTIAL_PROPERTY_TYPES = new Set([
  "Flat",
  "Terraced House",
  "Semi-Detached House",
  "Detached House",
  "House",
])
const COMMERCIAL_UNIT_TYPES = new Set([
  "Agricultural",
  "B&B / Guest House",
  "Industrial",
  "Land",
  "Office",
  "Retail",
])

const STATUS_BADGE = {
  Tenanted: "default",
  Vacant: "destructive",
  "Owner Occupied": "secondary",
  Unknown: "outline",
}

const DOCUMENT_UPLOADS = [
  { label: "Upload Brochure", documentType: "brochure", helper: "PDF, TXT, or CSV only" },
  { label: "Upload EPC", documentType: "epc", helper: "PDF, TXT, or CSV only" },
  { label: "Upload Rental Schedule", documentType: "rental_schedule", helper: "PDF, TXT, or CSV only" },
]
const DOCUMENT_ACCEPT = "application/pdf,text/plain,text/csv,.pdf,.txt,.csv"
const DOCUMENT_FILE_TYPES = new Set(["application/pdf", "text/plain", "text/csv"])
const FLOORPLAN_UPLOAD = {
  category: "media",
  documentType: "floorplan",
  title: "Floorplan",
}
const MEDIA_UPLOAD = {
  category: "media",
}
const FOREGROUND_BUTTON_CLASSNAME = "bg-foreground text-background hover:bg-foreground/90"
const PROPERTY_TYPES = ["Mixed-use", "Commercial", "Residential (C3)", "HMO (C4)"]
const TENURE_OPTIONS = ["Freehold", "Leasehold", "Share of Freehold"]
const UNIT_STATUSES = ["Tenanted", "Vacant", "Owner Occupied", "Unknown"]
const PLANNING_CLASSES = ["C3", "C4", "E", "Sui Generis"]
const TENANCY_TYPES = [
  "AST Fixed",
  "AST Periodic",
  "Commercial Lease",
  "Licence",
  "Owner Occupier",
  "Vacant / Unknown",
]
const RENT_MODE_OPTIONS = [
  ["monthly", "Monthly"],
  ["annual", "Annual"],
]

function formatCurrency(value) {
  return `£${Number(value).toLocaleString()}`
}

function rentModeForUnitType(unitType) {
  return COMMERCIAL_UNIT_TYPES.has(unitType) ? "annual" : "monthly"
}

function physicalProfileFromUnit(unit) {
  const explicitType = unit?.physicalPropertyType
  if (explicitType) {
    return {
      physicalPropertyType: explicitType,
      bedroomCount: unit.bedroomCount ? String(unit.bedroomCount) : "",
    }
  }

  const combinedType = String(unit?.type ?? "")
  const bedrooms = combinedType.match(/\b(\d+)[- ]?bed\b/i)?.[1] ?? ""
  const physicalPropertyType = PHYSICAL_PROPERTY_TYPES.find((type) => (
    combinedType.toLowerCase().includes(type.toLowerCase())
  )) ?? combinedType

  return { physicalPropertyType, bedroomCount: bedrooms }
}

function combinedUnitType(physicalPropertyType, bedroomCount) {
  if (!RESIDENTIAL_PROPERTY_TYPES.has(physicalPropertyType)) return physicalPropertyType

  const bedrooms = String(bedroomCount ?? "").trim()
  return bedrooms ? `${bedrooms}-Bed ${physicalPropertyType}` : physicalPropertyType
}

function postcodeFromAddress(address) {
  const match = String(address ?? "").toUpperCase().match(/\b([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})\b/)

  return match ? `${match[1]} ${match[2]}` : ""
}

function roundCurrency(value) {
  return Math.round(Number(value || 0) * 100) / 100
}

function monthlyRentFromAnnual(annualRent) {
  return roundCurrency(annualRent / 12)
}

function annualRentFromMonthly(monthlyRent) {
  return roundCurrency(monthlyRent * 12)
}

function createInitialUnits() {
  return DEFAULT_UNITS.map((unit) => ({ ...unit }))
}

function emptyEnrichmentFor(deal, useMockDefaults = true) {
  return {
    listingUrl: deal.sourceUrl || (useMockDefaults ? "https://www.rightmove.co.uk/properties/12345678" : ""),
    askingPrice: deal.price ?? "",
    agentName: useMockDefaults ? "Knight Frank" : "",
    agentContact: useMockDefaults ? "james.smith@knightfrank.com" : "",
    propertyType: "Mixed-use",
    bedroomCount: "",
    fullPostcode: postcodeFromAddress(deal.address),
    tenure: "Freehold",
    approxSizeSqFt: useMockDefaults ? "2,300" : "",
    floorplanDetected: useMockDefaults,
    currentIncomeConfirmed: false,
  }
}

function normalizeEnrichment(deal, enrichment = {}, useMockDefaults = true) {
  const defaults = emptyEnrichmentFor(deal, useMockDefaults)

  return {
    ...defaults,
    listingUrl: enrichment.listingUrl ?? defaults.listingUrl,
    askingPrice: enrichment.askingPrice ?? defaults.askingPrice,
    agentName: enrichment.agentName ?? defaults.agentName,
    agentContact: enrichment.agentContact ?? defaults.agentContact,
    propertyType: enrichment.propertyType ?? defaults.propertyType,
    bedroomCount: enrichment.bedroomCount ?? defaults.bedroomCount,
    fullPostcode: enrichment.fullPostcode ?? defaults.fullPostcode,
    tenure: enrichment.tenure ?? defaults.tenure,
    approxSizeSqFt: enrichment.approxSizeSqFt ?? defaults.approxSizeSqFt,
    floorplanDetected: enrichment.floorplanDetected ?? defaults.floorplanDetected,
    currentIncomeConfirmed: enrichment.currentIncomeConfirmed ?? defaults.currentIncomeConfirmed,
  }
}

function sameEnrichment(left, right) {
  return JSON.stringify(left) === JSON.stringify(right)
}

function normalizeUnits(units) {
  return units.map((unit) => {
    const profile = physicalProfileFromUnit(unit)

    return {
      ...unit,
      ...profile,
      rent: Number(unit.rent ?? 0),
      annualRent: annualRentValue(unit.annualRent, unit.rent),
    }
  })
}

function isFloorplanFile(file) {
  return file.documentType?.toLowerCase() === "floorplan"
}

function documentTypeForFile(file, upload) {
  if (upload.documentType) return upload.documentType
  if (file.type?.startsWith("image/")) return "image"

  return "other"
}

function titleForFile(file, upload) {
  return upload.title || upload.label?.replace("Upload ", "") || file.name
}

function isDocumentUpload(upload) {
  return (upload.category || "document") === "document"
}

function isSupportedDocumentFile(file) {
  const extension = file.name.split(".").pop()?.toLowerCase()
  return DOCUMENT_FILE_TYPES.has(file.type) || ["pdf", "txt", "csv"].includes(extension)
}

function normalizeGalleryImage(item, index, fallbackAlt) {
  const image = typeof item === "string" ? { url: item } : item
  const url = image?.url || image?.src || image?.image

  if (!url) return null

  return {
    id: image.id || `${url}-${index}`,
    url,
    alt: image.alt || fallbackAlt || `Property media ${index + 1}`,
    label: image.label || image.title || `Image ${index + 1}`,
    sourceLabel: image.sourceLabel || (isMediaFile(image) ? "Uploaded" : "Property image"),
  }
}

function isImageFile(file) {
  const contentType = file.contentType?.toLowerCase() || ""
  const documentType = file.documentType?.toLowerCase() || ""

  return contentType.startsWith("image/") || documentType === "image"
}

function isMediaFile(file) {
  const category = file.category?.toLowerCase() || ""
  const documentType = file.documentType?.toLowerCase() || ""

  return category === "media" || ["image", "floorplan"].includes(documentType)
}

function normalizeUploadedGalleryImage(file, index, fallbackAlt) {
  if (!file.url || !isMediaFile(file) || !isImageFile(file)) return null

  const label = file.title || file.filename || file.documentType || `Uploaded media ${index + 1}`

  return {
    id: `uploaded-${file.id || file.url}`,
    fileId: file.id,
    url: file.url,
    alt: label || fallbackAlt || `Uploaded property media ${index + 1}`,
    label,
    sourceLabel: "Uploaded",
    cover: file.cover === true,
  }
}

function extractionStatusBadge(file) {
  const status = file.extractionStatus
  const label = file.extractionStatusLabel || status
  if (!status || !label) return null

  if (status === "extracted") {
    return {
      label,
      className: "border-primary/20 bg-primary/10 text-primary",
    }
  }

  if (status === "failed") {
    return {
      label,
      variant: "destructive",
    }
  }

  return {
    label,
    variant: "outline",
    className: "text-muted-foreground",
  }
}

function DocumentFileRow({ file, onDelete }) {
  const extractionBadge = extractionStatusBadge(file)

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-bold text-foreground">{file.title}</p>
        <p className="text-xs font-medium text-muted-foreground">
          {file.documentType || file.category || "Document"}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Badge variant="outline">Uploaded</Badge>
        {extractionBadge ? (
          <Badge
            variant={extractionBadge.variant || "outline"}
            className={extractionBadge.className}
          >
            {extractionBadge.label}
          </Badge>
        ) : null}
        {file.url ? (
          <Button asChild variant="ghost" size="sm">
            <a href={file.url} target="_blank" rel="noreferrer">
              View
            </a>
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={`Delete document ${file.title || file.filename || "document"}`}
          onClick={() => onDelete(file)}
        >
          <Trash2 aria-hidden="true" className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

function uniqueGalleryImages(images) {
  const seenUrls = new Set()

  return images.filter((image) => {
    if (!image) return false

    if (seenUrls.has(image.url)) return false
    seenUrls.add(image.url)
    return true
  })
}

function galleryImagesForDeal(deal, files = []) {
  const uploadedImages = files
    .map((file, index) => normalizeUploadedGalleryImage(file, index, deal.address))
    .filter(Boolean)
  const suppliedImages = [
    ...(Array.isArray(deal.images) ? deal.images : []),
    ...(Array.isArray(deal.galleryImages) ? deal.galleryImages : []),
  ]
  const primaryImage = deal.primaryImage?.url ? deal.primaryImage : null
  const listingImage = deal.imageIsPlaceholder ? null : deal.image

  if (suppliedImages.length > 0) {
    return uniqueGalleryImages([...uploadedImages, primaryImage, listingImage, ...suppliedImages]
      .map((image, index) => normalizeGalleryImage(image, index, deal.address))
      .filter(Boolean))
  }

  const primaryGalleryImage = normalizeGalleryImage(primaryImage, 0, deal.address)
  if (uploadedImages.length > 0 || primaryGalleryImage) {
    const listingGalleryImage = normalizeGalleryImage(listingImage, 1, deal.address)

    return uniqueGalleryImages([primaryGalleryImage, ...uploadedImages, listingGalleryImage])
  }

  const listingGalleryImage = normalizeGalleryImage(listingImage, 0, deal.address)

  return uniqueGalleryImages([listingGalleryImage])
}

function primaryImageFromFiles(files) {
  return files.find((file) => file.cover && isImageFile(file)) ||
    files.find((file) => file.documentType === "Image" && isImageFile(file)) ||
    files.find(isImageFile) ||
    null
}

function initialUnitForm(unit) {
  const source = unit ?? EMPTY_UNIT

  return { ...source, ...physicalProfileFromUnit(source) }
}

function isBlank(value) {
  return value === "" || value === null || value === undefined
}

function annualRentValue(annualRent, rent) {
  if (!isBlank(annualRent)) return Number(annualRent)

  return annualRentFromMonthly(rent)
}

function annualRentFor(unit) {
  return annualRentValue(unit.annualRent, unit.rent)
}

function calculateIncomeMetrics(units) {
  const totalMonthlyIncome = units.reduce((sum, unit) => sum + Number(unit.rent || 0), 0)
  const totalAnnualIncome = units.reduce((sum, unit) => sum + annualRentFor(unit), 0)
  const tenantedUnits = units.filter((unit) => unit.status === "Tenanted").length
  const occupancyRate = units.length ? Math.round((tenantedUnits / units.length) * 100) : 0
  const vacancyRate = units.length ? 100 - occupancyRate : 0

  return { totalMonthlyIncome, totalAnnualIncome, occupancyRate, vacancyRate }
}

function newUnitRecord(unitData) {
  return {
    ...unitData,
    id: Date.now(),
    annualRent: annualRentValue(unitData.annualRent, unitData.rent),
  }
}

function savedUnitRecord(formData) {
  const rent = Number(formData.rent ?? 0)
  const type = combinedUnitType(formData.physicalPropertyType, formData.bedroomCount)

  return {
    ...formData,
    type,
    unit: formData.unit.trim() || "New Unit",
    rent,
    annualRent: annualRentValue(formData.annualRent, rent),
  }
}

function SectionHeading({ icon: Icon, children }) {
  return (
    <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-foreground">
      <Icon aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
      {children}
    </h3>
  )
}

export function DealEnrichmentTab({ deal, onComplete, onDealChange }) {
  const { currentUser, csrfToken } = useSession()
  const shouldPersist = Boolean(currentUser && looksPersistedDealId(deal.id))
  const [enrichment, setEnrichment] = useState(() => normalizeEnrichment(deal, {}, !shouldPersist))
  const savedEnrichmentRef = useRef(enrichment)
  const [units, setUnits] = useState(() => (shouldPersist ? [] : createInitialUnits()))
  const [files, setFiles] = useState([])
  const [error, setError] = useState(null)
  const [unitModalOpen, setUnitModalOpen] = useState(false)
  const [editingUnit, setEditingUnit] = useState(null)
  const [unitToDelete, setUnitToDelete] = useState(null)
  const [fileToDelete, setFileToDelete] = useState(null)
  const [fileDeletePending, setFileDeletePending] = useState(false)
  const [selectedGalleryIndex, setSelectedGalleryIndex] = useState(0)
  const [galleryPreviewOpen, setGalleryPreviewOpen] = useState(false)
  const { totalMonthlyIncome, totalAnnualIncome, occupancyRate, vacancyRate } = calculateIncomeMetrics(units)
  const hasCurrentIncome = totalMonthlyIncome > 0 || totalAnnualIncome > 0
  const floorplanFile = files.find(isFloorplanFile)
  const hasFloorplanSignal = Boolean(enrichment.floorplanDetected || floorplanFile)
  const galleryImages = galleryImagesForDeal(deal, files)
  const activeGalleryIndex = galleryImages.length > 0
    ? Math.min(selectedGalleryIndex, galleryImages.length - 1)
    : 0
  const selectedGalleryImage = galleryImages[activeGalleryIndex]
  const selectedUploadedFile = selectedGalleryImage?.fileId
    ? files.find((file) => file.id === selectedGalleryImage.fileId)
    : null
  const deletingMediaFile = fileToDelete ? isMediaFile(fileToDelete) : false
  const fileToDeleteLabel = fileToDelete?.title || fileToDelete?.filename || (deletingMediaFile ? "this image" : "this document")

  useEffect(() => {
    if (!shouldPersist) return undefined

    let ignore = false

    getDealEnrichment(deal.id, { csrfToken })
      .then((payload) => {
        if (ignore) return
        const nextEnrichment = normalizeEnrichment({
          price: deal.price,
          sourceUrl: deal.sourceUrl,
        }, payload.enrichment, false)
        savedEnrichmentRef.current = nextEnrichment
        setEnrichment(nextEnrichment)
        setUnits(normalizeUnits(payload.units ?? []))
        setFiles(payload.files ?? [])
      })
      .catch((loadError) => {
        if (!ignore) setError(loadError.message)
      })

    return () => { ignore = true }
  }, [csrfToken, deal.id, deal.price, deal.sourceUrl, shouldPersist])

  const handleOpenUnitModal = (unit = null) => {
    setEditingUnit(unit)
    setUnitModalOpen(true)
  }

  const applyEnrichmentPayload = (payload, { navigateOnComplete = false } = {}) => {
    const nextEnrichment = normalizeEnrichment(deal, payload.enrichment, !shouldPersist)
    savedEnrichmentRef.current = nextEnrichment
    setEnrichment(nextEnrichment)
    setUnits(normalizeUnits(payload.units ?? []))
    setFiles(payload.files ?? [])
    if (navigateOnComplete && payload.complete) onComplete?.()
  }

  const syncDealMedia = (nextFiles, nextDeal = null) => {
    if (nextDeal) {
      onDealChange?.(nextDeal)
      return
    }

    const primaryImage = primaryImageFromFiles(nextFiles)
    onDealChange?.({
      ...deal,
      mediaFiles: nextFiles,
      floorplanFile: nextFiles.find(isFloorplanFile) ?? null,
      primaryImage,
      image: primaryImage?.url ?? deal.image,
    })
  }

  const handleEnrichmentChange = (field, value) => {
    setEnrichment((current) => ({ ...current, [field]: value }))
  }

  const persistEnrichment = async (updates = {}, { navigateOnComplete = false } = {}) => {
    const nextEnrichment = { ...enrichment, ...updates }
    setEnrichment(nextEnrichment)

    if (Object.keys(updates).length === 0 && sameEnrichment(nextEnrichment, savedEnrichmentRef.current)) {
      if (navigateOnComplete && nextEnrichment.currentIncomeConfirmed) onComplete?.()
      return
    }

    if (!shouldPersist) {
      if (navigateOnComplete && nextEnrichment.currentIncomeConfirmed) onComplete?.()
      return
    }

    try {
      setError(null)
      const payload = await updateDealEnrichment(deal.id, {
        ...nextEnrichment,
        ...updates,
        activitySource: "manual",
      }, { csrfToken })
      applyEnrichmentPayload(payload, { navigateOnComplete })
    } catch (saveError) {
      setError(saveError.message)
    }
  }

  const handleSaveUnit = async (unitData) => {
    const savedUnit = savedUnitRecord(unitData)

    if (!shouldPersist) {
      if (editingUnit) {
        setUnits((currentUnits) =>
          currentUnits.map((unit) =>
            unit.id === editingUnit.id ? { ...savedUnit, id: editingUnit.id } : unit,
          ),
        )
      } else {
        setUnits((currentUnits) => [...currentUnits, newUnitRecord(savedUnit)])
        onComplete?.()
      }

      setUnitModalOpen(false)
      setEditingUnit(null)
      return
    }

    try {
      setError(null)
      const payload = editingUnit
        ? await updateDealUnit(deal.id, editingUnit.id, savedUnit, { csrfToken })
        : await createDealUnit(deal.id, savedUnit, { csrfToken })

      setUnits((currentUnits) => {
        const nextUnit = payload.unit
        if (editingUnit) {
          return currentUnits.map((unit) => (unit.id === editingUnit.id ? nextUnit : unit))
        }

        return [...currentUnits, nextUnit]
      })
      if (!editingUnit) onComplete?.()
      setUnitModalOpen(false)
      setEditingUnit(null)
    } catch (saveError) {
      setError(saveError.message)
    }
  }

  const handleDeleteUnit = async (unitId) => {
    if (!shouldPersist) {
      setUnits((currentUnits) => currentUnits.filter((unit) => unit.id !== unitId))
      return
    }

    await archiveDealUnit(deal.id, unitId, { csrfToken })
    setUnits((currentUnits) => currentUnits.filter((unit) => unit.id !== unitId))
  }

  const handleConfirmDeleteUnit = async () => {
    if (!unitToDelete) return

    try {
      setError(null)
      await handleDeleteUnit(unitToDelete.id)
      setUnitToDelete(null)
    } catch (deleteError) {
      setError(deleteError.message)
    }
  }

  const handleConfirmNoCurrentIncome = () => {
    persistEnrichment({ currentIncomeConfirmed: true }, { navigateOnComplete: true })
  }

  const handleSetCoverFile = async (file) => {
    if (!file?.id || !isImageFile(file)) return

    const nextFiles = files.map((item) => ({ ...item, cover: item.id === file.id }))
    setFiles(nextFiles)
    syncDealMedia(nextFiles)

    if (!shouldPersist) return

    try {
      setError(null)
      const payload = await setDealCoverFile(deal.id, file.id, { csrfToken })
      if (payload.file) {
        const persistedFiles = files.map((item) => ({ ...item, cover: item.id === payload.file.id }))
        setFiles(persistedFiles)
        syncDealMedia(persistedFiles, payload.deal)
      }
    } catch (coverError) {
      setError(coverError.message)
    }
  }

  const handleDeleteFile = async (file) => {
    if (!file?.id) return false

    if (!shouldPersist) {
      const nextFiles = files.filter((item) => item.id !== file.id)
      setFiles(nextFiles)
      syncDealMedia(nextFiles)
      return true
    }

    try {
      setError(null)
      await archiveDealFile(deal.id, file.id, { csrfToken })
      const nextFiles = files.filter((item) => item.id !== file.id)
      setFiles(nextFiles)
      syncDealMedia(nextFiles)
      return true
    } catch (deleteError) {
      setError(deleteError.message)
      return false
    }
  }

  const handleConfirmDeleteFile = async (event) => {
    event.preventDefault()
    if (!fileToDelete || fileDeletePending) return

    setFileDeletePending(true)
    try {
      const deleted = await handleDeleteFile(fileToDelete)
      if (deleted) setFileToDelete(null)
    } finally {
      setFileDeletePending(false)
    }
  }

  const handleFilesSelected = async (selectedFiles, upload) => {
    if (!selectedFiles.length) return
    const invalidDocument = isDocumentUpload(upload) && selectedFiles.find((file) => !isSupportedDocumentFile(file))
    if (invalidDocument) {
      setError(`${upload.label} supports PDF or text files only`)
      return
    }

    if (!shouldPersist) {
      const localFiles = selectedFiles.map((file) => ({
        id: `${upload.documentType || "media"}-${file.name}-${Date.now()}`,
        title: titleForFile(file, upload),
        documentType: upload.label || upload.title || "Media",
      }))
      const nextFiles = [...localFiles, ...files]
      setFiles(nextFiles)
      syncDealMedia(nextFiles)
      return
    }

    try {
      setError(null)
      const uploadedFiles = []

      for (const file of selectedFiles) {
        const payload = await uploadDealFile(deal.id, {
          category: upload.category || "document",
          documentType: documentTypeForFile(file, upload),
          title: titleForFile(file, upload),
          file,
          activitySource: "manual",
        }, { csrfToken })
        uploadedFiles.push(payload.file)
      }

      const nextFiles = [...uploadedFiles, ...files]
      setFiles(nextFiles)
      syncDealMedia(nextFiles)
    } catch (uploadError) {
      setError(uploadError.message)
    }
  }

  const handleFileInputChange = (event, upload) => {
    handleFilesSelected(Array.from(event.target.files ?? []), upload)
    event.target.value = ""
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 pb-16">
      {error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
          {error}
        </div>
      ) : null}

      <section className="rounded-lg border border-border bg-card p-5 text-card-foreground shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1">
            <Label htmlFor="listing-url" className="sr-only">Listing URL</Label>
            <LinkIcon
              aria-hidden="true"
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="listing-url"
              type="url"
              value={enrichment.listingUrl}
              className="pl-9"
              onBlur={() => persistEnrichment()}
              onChange={(event) => handleEnrichmentChange("listingUrl", event.target.value)}
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            className={FOREGROUND_BUTTON_CLASSNAME}
            onClick={() => persistEnrichment({ listingImported: true })}
          >
            Import Listing
          </Button>
        </div>

        <div className="mt-5 grid gap-4 border-t border-border pt-4 md:grid-cols-3">
          <Field
            label="Asking Price (£)"
            id="asking-price"
            value={String(enrichment.askingPrice ?? "")}
            onBlur={() => persistEnrichment()}
            onChange={(value) => handleEnrichmentChange("askingPrice", value)}
          />
          <Field
            label="Agent Name (Optional)"
            id="agent-name"
            value={enrichment.agentName}
            onBlur={() => persistEnrichment()}
            onChange={(value) => handleEnrichmentChange("agentName", value)}
          />
          <Field
            label="Agent Contact (Optional)"
            id="agent-contact"
            value={enrichment.agentContact}
            onBlur={() => persistEnrichment()}
            onChange={(value) => handleEnrichmentChange("agentContact", value)}
          />
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeading icon={ImageIcon}>Media</SectionHeading>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)]">
          <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
            {selectedGalleryImage ? (
              <div className="relative">
                <button
                  type="button"
                  aria-label={`View ${selectedGalleryImage.label.toLowerCase()}`}
                  className="relative block aspect-[16/7] w-full overflow-hidden rounded-md bg-muted text-left"
                  onClick={() => setGalleryPreviewOpen(true)}
                >
                  <img
                    src={selectedGalleryImage.url}
                    alt={selectedGalleryImage.alt}
                    className={cn(
                      "h-full w-full transition-transform hover:scale-[1.01]",
                      selectedGalleryImage.sourceLabel === "Uploaded" ? "object-contain" : "object-cover",
                    )}
                  />
                  <Badge
                    variant="secondary"
                    className="absolute left-3 top-3 rounded-sm bg-foreground/80 text-background"
                  >
                    {selectedGalleryImage.cover ? "Cover photo" : selectedGalleryImage.sourceLabel}
                  </Badge>
                </button>
                {selectedUploadedFile ? (
                  <div className="absolute bottom-3 right-3 flex flex-wrap justify-end gap-2">
                    {!selectedUploadedFile.cover && isImageFile(selectedUploadedFile) ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="bg-background/90 text-foreground hover:bg-background"
                        onClick={() => handleSetCoverFile(selectedUploadedFile)}
                      >
                        Set cover
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="destructive"
                      aria-label={`Delete ${selectedUploadedFile.title || selectedUploadedFile.filename || "image"}`}
                      onClick={() => setFileToDelete(selectedUploadedFile)}
                    >
                      <Trash2 aria-hidden="true" className="h-4 w-4" />
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="flex aspect-[16/7] items-center justify-center rounded-md bg-muted text-sm font-medium text-muted-foreground">
                No media imported
              </div>
            )}
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {galleryImages.map((image, index) => (
                <button
                  key={image.id}
                  type="button"
                  aria-label={`Select image ${index + 1}`}
                  aria-pressed={activeGalleryIndex === index}
                  className={cn(
                    "h-14 w-20 shrink-0 overflow-hidden rounded-md border-2 border-transparent transition-opacity hover:opacity-100",
                    activeGalleryIndex === index && "border-primary",
                  )}
                  onClick={() => setSelectedGalleryIndex(index)}
                >
                  <img src={image.url} alt="" className="h-full w-full object-cover opacity-80" />
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
            <FloorplanTile
              floorplanFile={floorplanFile}
              hasFloorplanSignal={hasFloorplanSignal}
              onFilesSelected={handleFilesSelected}
            />
            <MediaUploadTile onFilesSelected={handleFilesSelected} />
          </div>
        </div>
      </section>

      <section className="space-y-4 rounded-lg border border-border bg-card p-5 text-card-foreground shadow-sm">
        <SectionHeading icon={Info}>Property Details</SectionHeading>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <SelectField
            label="Property Type"
            value={enrichment.propertyType}
            options={PROPERTY_TYPES}
            onChange={(value) => persistEnrichment({ propertyType: value })}
          />
          <Field
            label="Full Postcode"
            id="full-postcode"
            placeholder="e.g. CH1 3JW"
            value={String(enrichment.fullPostcode ?? "")}
            onBlur={() => persistEnrichment()}
            onChange={(value) => handleEnrichmentChange("fullPostcode", value)}
          />
          <SelectField
            label="Tenure"
            value={enrichment.tenure}
            options={TENURE_OPTIONS}
            onChange={(value) => persistEnrichment({ tenure: value })}
          />
          <Field
            label="Approx Size (sq ft)"
            id="approx-size"
            value={String(enrichment.approxSizeSqFt ?? "")}
            onBlur={() => persistEnrichment()}
            onChange={(value) => handleEnrichmentChange("approxSizeSqFt", value)}
          />
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <SectionHeading icon={Building}>Current Income Model</SectionHeading>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className={FOREGROUND_BUTTON_CLASSNAME}
            onClick={() => handleOpenUnitModal()}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            Add Unit
          </Button>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                <TableHead className="px-5 text-xs font-bold uppercase tracking-widest text-muted-foreground">Unit</TableHead>
                <TableHead className="px-5 text-xs font-bold uppercase tracking-widest text-muted-foreground">Type</TableHead>
                <TableHead className="px-5 text-xs font-bold uppercase tracking-widest text-muted-foreground">Rent</TableHead>
                <TableHead className="px-5 text-xs font-bold uppercase tracking-widest text-muted-foreground">Status</TableHead>
                <TableHead className="px-5 text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {units.length ? (
                units.map((unit) => (
                  <TableRow key={unit.id} className="cursor-pointer" onClick={() => handleOpenUnitModal(unit)}>
                    <TableCell className="px-5 py-4 font-bold text-foreground">{unit.unit}</TableCell>
                    <TableCell className="px-5 py-4 font-medium text-muted-foreground">{unit.type}</TableCell>
                    <TableCell className="px-5 py-4">
                      <span className="block font-bold text-foreground">{formatCurrency(unit.rent)} /mo</span>
                      <span className="mt-0.5 block text-xs font-medium text-muted-foreground">
                        {formatCurrency(annualRentFor(unit))} /yr
                      </span>
                    </TableCell>
                    <TableCell className="px-5 py-4">
                      <Badge variant={STATUS_BADGE[unit.status] ?? "outline"}>{unit.status}</Badge>
                    </TableCell>
                    <TableCell className="px-5 py-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          aria-label={`Delete ${unit.unit}`}
                          onClick={(event) => {
                            event.stopPropagation()
                            setUnitToDelete(unit)
                          }}
                        >
                          <Trash2 aria-hidden="true" className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon-xs" aria-label={`Edit ${unit.unit}`}>
                          <ChevronRight aria-hidden="true" className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="px-5 py-8 text-center text-sm font-medium text-muted-foreground">
                    No current income units captured yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="grid gap-4 rounded-lg border border-border bg-card p-5 shadow-sm md:grid-cols-3">
          <Metric label="Total Monthly" value={formatCurrency(totalMonthlyIncome)} />
          <Metric label="Total Annual" value={formatCurrency(totalAnnualIncome)} />
          <Metric
            label="Occupancy Rate"
            value={`${occupancyRate}%`}
            detail={vacancyRate > 0 ? `${vacancyRate}% Vacant` : null}
          />
        </div>

        {!hasCurrentIncome ? (
          <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-bold text-foreground">No current income</p>
              <p className="text-xs font-medium text-muted-foreground">
                Confirm this if the property is vacant or income is not applicable.
              </p>
            </div>
            <Button
              type="button"
              variant={enrichment.currentIncomeConfirmed ? "outline" : "secondary"}
              className={cn(!enrichment.currentIncomeConfirmed && FOREGROUND_BUTTON_CLASSNAME)}
              onClick={handleConfirmNoCurrentIncome}
            >
              {enrichment.currentIncomeConfirmed ? "Confirmed" : "Confirm No Income"}
            </Button>
          </div>
        ) : null}
      </section>

      <section className="space-y-4 rounded-lg border border-border bg-card p-5 shadow-sm">
        <SectionHeading icon={FileText}>Documents</SectionHeading>
        <div className="grid gap-3 md:grid-cols-3">
          {DOCUMENT_UPLOADS.map((upload) => (
            <DocumentUploadButton
              key={upload.documentType}
              upload={upload}
              onFileInputChange={handleFileInputChange}
            />
          ))}
        </div>
        {files.length ? (
          <div className="divide-y divide-border rounded-lg border border-border">
            {files.map((file) => (
              <DocumentFileRow key={file.id} file={file} onDelete={setFileToDelete} />
            ))}
          </div>
        ) : null}
      </section>

      <Dialog open={galleryPreviewOpen} onOpenChange={setGalleryPreviewOpen}>
        <DialogContent className="overflow-hidden p-0 sm:max-w-5xl">
          <DialogHeader className="sr-only">
            <DialogTitle>Media preview</DialogTitle>
            <DialogDescription>{selectedGalleryImage?.label || "Selected property media"}</DialogDescription>
          </DialogHeader>
          {selectedGalleryImage ? (
            <img
              src={selectedGalleryImage.url}
              alt={selectedGalleryImage.alt}
              className="max-h-[80vh] w-full object-contain"
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {unitModalOpen ? (
        <UnitDialog
          key={editingUnit?.id ?? "new-unit"}
          unit={editingUnit}
          open={unitModalOpen}
          onOpenChange={(open) => {
            setUnitModalOpen(open)
            if (!open) setEditingUnit(null)
          }}
          onSave={handleSaveUnit}
        />
      ) : null}

      <AlertDialog open={!!unitToDelete} onOpenChange={(open) => !open && setUnitToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Unit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {unitToDelete?.unit}? This action cannot be undone and will immediately update the total income and occupancy calculations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={handleConfirmDeleteUnit}
            >
              Confirm Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!fileToDelete}
        onOpenChange={(open) => !open && !fileDeletePending && setFileToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{deletingMediaFile ? "Delete Image" : "Delete Document"}</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingMediaFile
                ? `Are you sure you want to delete ${fileToDeleteLabel}? This removes it from the media gallery and deal thumbnails.`
                : `Delete ${fileToDeleteLabel}? This removes it from active deal evidence and may require related analysis to be refreshed.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={fileDeletePending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={fileDeletePending}
              onClick={handleConfirmDeleteFile}
            >
              {fileDeletePending ? "Deleting…" : (deletingMediaFile ? "Confirm Delete" : "Delete Document")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function Field({
  label,
  id,
  value,
  onBlur,
  onChange,
  type = "text",
  inputMode,
  min,
  step,
  placeholder,
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[10px] font-bold uppercase tracking-widest text-foreground">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        inputMode={inputMode}
        min={min}
        step={step}
        placeholder={placeholder}
        value={value}
        className="font-medium placeholder:font-normal placeholder:text-muted-foreground placeholder:opacity-50"
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

function SelectField({ label, value, options, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] font-bold uppercase tracking-widest text-foreground">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-label={label} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function Metric({ label, value, detail = null }) {
  return (
    <div>
      <span className="block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className="mt-1 block text-2xl font-bold text-foreground">{value}</span>
      {detail ? (
        <Badge variant="destructive" className="mt-2 rounded-sm text-[10px] uppercase tracking-widest">
          {detail}
        </Badge>
      ) : null}
    </div>
  )
}

function openFileUrl(url) {
  if (url) window.open(url, "_blank", "noopener,noreferrer")
}

function FloorplanTile({ floorplanFile, hasFloorplanSignal, onFilesSelected }) {
  const inputRef = useRef(null)
  const canViewFloorplan = Boolean(floorplanFile?.url)

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        aria-label="Upload floorplan file"
        className="sr-only"
        onChange={(event) => {
          onFilesSelected(Array.from(event.target.files ?? []), FLOORPLAN_UPLOAD)
          event.target.value = ""
        }}
      />
      <button
        type="button"
        aria-label={canViewFloorplan ? "View floorplan" : "Upload floorplan"}
        className="flex min-h-40 flex-col items-center justify-center rounded-lg border border-primary/20 bg-primary/5 p-4 text-center shadow-sm transition-colors hover:bg-primary/10"
        onClick={() => (canViewFloorplan ? openFileUrl(floorplanFile.url) : inputRef.current?.click())}
      >
        <LayoutGrid aria-hidden="true" className="mb-3 h-8 w-8 text-primary" />
        <span className="text-sm font-bold text-foreground">
          {hasFloorplanSignal ? "Floorplan detected" : "Upload floorplan"}
        </span>
        <span className="mt-1 text-xs font-medium text-primary">
          {canViewFloorplan ? "Click to view" : "PDF, JPG, PNG"}
        </span>
      </button>
    </>
  )
}

function MediaUploadTile({ onFilesSelected }) {
  const inputRef = useRef(null)

  const handleDrop = (event) => {
    event.preventDefault()
    onFilesSelected(Array.from(event.dataTransfer.files ?? []), MEDIA_UPLOAD)
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        aria-label="Upload media files"
        className="sr-only"
        multiple
        onChange={(event) => {
          onFilesSelected(Array.from(event.target.files ?? []), MEDIA_UPLOAD)
          event.target.value = ""
        }}
      />
      <button
        type="button"
        aria-label="Upload media"
        className="flex min-h-40 flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-card p-4 text-center shadow-sm transition-colors hover:border-primary/50 hover:bg-primary/5"
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
      >
        <UploadCloud aria-hidden="true" className="mb-3 h-6 w-6 text-muted-foreground" />
        <span className="text-xs font-bold text-foreground">Drag & drop media</span>
        <span className="mt-1 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
          Images, PDFs, Brochures
        </span>
      </button>
    </>
  )
}

function DocumentUploadButton({ upload, onFileInputChange }) {
  const inputRef = useRef(null)

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={DOCUMENT_ACCEPT}
        aria-label={`${upload.label} file`}
        className="sr-only"
        onChange={(event) => onFileInputChange(event, upload)}
      />
      <button
        type="button"
        aria-label={upload.label}
        className="flex h-28 flex-col items-center justify-center rounded-lg border border-dashed border-border text-center transition-colors hover:border-primary/50 hover:bg-primary/5"
        onClick={() => inputRef.current?.click()}
      >
        <FileText aria-hidden="true" className="mb-2 h-6 w-6 text-muted-foreground" />
        <span className="text-xs font-bold text-foreground">{upload.label}</span>
        <span className="mt-1 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
          {upload.helper}
        </span>
      </button>
    </>
  )
}

function DialogSection({ title, children }) {
  return (
    <section>
      <h3 className="mb-4 border-b border-border/50 pb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  )
}

function RentModeControl({ value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] font-bold uppercase tracking-widest text-foreground">Rent Entry</Label>
      <div className="grid grid-cols-2 rounded-md border border-border bg-muted p-1">
        {RENT_MODE_OPTIONS.map(([mode, label]) => (
          <button
            key={mode}
            type="button"
            aria-pressed={value === mode}
            className={cn(
              "rounded-sm px-3 py-2 text-xs font-bold uppercase tracking-widest text-muted-foreground transition-colors",
              value === mode && "bg-background text-foreground shadow-sm",
            )}
            onClick={() => onChange(mode)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  )
}

function RentSummaryItem({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm font-bold text-foreground">{value}</dd>
    </div>
  )
}

function UnitDialog({ unit, open, onOpenChange, onSave }) {
  const [formData, setFormData] = useState(() => initialUnitForm(unit))
  const [rentMode, setRentMode] = useState(() => (
    rentModeForUnitType(initialUnitForm(unit).physicalPropertyType)
  ))

  const handleChange = (field, value) => {
    setFormData((current) => {
      const next = { ...current, [field]: value }
      return next
    })
  }

  const handleTypeChange = (value) => {
    setRentMode(rentModeForUnitType(value))
    setFormData((current) => ({
      ...current,
      physicalPropertyType: value,
      bedroomCount: RESIDENTIAL_PROPERTY_TYPES.has(value) ? current.bedroomCount : "",
    }))
  }

  const handleRentChange = (value) => {
    setFormData((current) => {
      if (rentMode === "annual") {
        return {
          ...current,
          annualRent: value,
          rent: monthlyRentFromAnnual(value),
        }
      }

      return {
        ...current,
        rent: value,
        annualRent: annualRentFromMonthly(value),
      }
    })
  }

  const handleSave = () => {
    onSave(savedUnitRecord(formData))
    setFormData(initialUnitForm())
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{unit ? "Edit Unit" : "Add Unit"}</DialogTitle>
          <DialogDescription>
            Capture the unit mix, tenancy status, and rent assumptions for this mock deal.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6">
          <DialogSection title="Core Details">
            <div className="grid gap-4 md:grid-cols-2">
              <ModalField label="Unit ID / Name" id="unit-name" value={formData.unit} onChange={(value) => handleChange("unit", value)} />
              <ModalSelect
                label="Property Type"
                value={formData.physicalPropertyType}
                options={PHYSICAL_PROPERTY_TYPES}
                onChange={handleTypeChange}
              />
              {RESIDENTIAL_PROPERTY_TYPES.has(formData.physicalPropertyType) ? (
                <ModalField
                  label="Bedroom Count"
                  id="unit-bedroom-count"
                  type="number"
                  value={formData.bedroomCount}
                  onChange={(value) => handleChange("bedroomCount", value)}
                />
              ) : null}
              <RentModeControl value={rentMode} onChange={setRentMode} />
              {rentMode === "annual" ? (
                <ModalField label="Annual Rent (£)" id="annual-rent" type="number" step="0.01" prefix="£" value={String(formData.annualRent || "")} onChange={handleRentChange} />
              ) : (
                <ModalField label="Monthly Rent (£)" id="monthly-rent" type="number" step="0.01" prefix="£" value={String(formData.rent || "")} onChange={handleRentChange} />
              )}
              <ModalSelect label="Occupation Status" value={formData.status} options={UNIT_STATUSES} onChange={(value) => handleChange("status", value)} />
            </div>
          </DialogSection>

          <div className="grid gap-8 md:grid-cols-2">
            <DialogSection title="Planning & Control">
              <ModalSelect label="Planning Class" value={formData.planning} options={PLANNING_CLASSES} onChange={(value) => handleChange("planning", value)} />
            </DialogSection>
            <DialogSection title="Physical">
              <ModalField label="Area (sq ft)" id="area" value={formData.area} onChange={(value) => handleChange("area", value)} />
            </DialogSection>
          </div>

          <section className="-mx-6 border-y border-primary/10 bg-primary/5 px-6 py-6">
            <h3 className="mb-4 border-b border-primary/10 pb-2 text-[10px] font-bold uppercase tracking-widest text-primary">Tenancy & Control</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <ModalSelect label="Tenancy Type" value={formData.tenancyType} options={TENANCY_TYPES} onChange={(value) => handleChange("tenancyType", value)} />
              <ModalField label="Notice Period (Months)" id="notice-period" type="number" value={formData.noticePeriod} onChange={(value) => handleChange("noticePeriod", value)} />
              <ModalField label="End of Tenancy" id="end-of-tenancy" type="date" value={formData.endOfTenancy} onChange={(value) => handleChange("endOfTenancy", value)} />
              <ModalField label="Break Clause" id="break-clause" type="date" value={formData.breakClause} onChange={(value) => handleChange("breakClause", value)} />
            </div>
          </section>

          <DialogSection title="Financial Summary">
            <dl className="grid gap-4 rounded-lg border border-border bg-card p-4 md:grid-cols-2">
              <RentSummaryItem label="Monthly equivalent" value={`${formatCurrency(formData.rent || 0)} /mo`} />
              <RentSummaryItem label="Annual equivalent" value={`${formatCurrency(formData.annualRent || 0)} /yr`} />
            </dl>
          </DialogSection>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" variant="secondary" className={FOREGROUND_BUTTON_CLASSNAME} onClick={handleSave}>
            Save Unit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ModalField({ label, id, type = "text", step = undefined, prefix = null, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[10px] font-bold uppercase tracking-widest text-foreground">
        {label}
      </Label>
      <div className="relative">
        {prefix ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground"
          >
            {prefix}
          </span>
        ) : null}
        <Input
          id={id}
          type={type}
          step={step}
          value={value}
          className={cn(prefix && "pl-7")}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
    </div>
  )
}

function ModalSelect({ label, value, options, onChange }) {
  const labelId = useId()

  return (
    <div className="space-y-1.5">
      <Label id={labelId} className="text-[10px] font-bold uppercase tracking-widest text-foreground">
        {label}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-labelledby={labelId} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
